import { useEffect, useState } from "react";
import api, { imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { PageHeader, Card } from "@/components/admin/Primitives";

/**
 * Generic admin resource manager (dark theme).
 */
export default function ResourceManager({ resource, title, subtitle, defaults, columns, renderForm }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/${resource}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [resource]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/admin/${resource}/${editing.id}`, stripMeta(editing));
      else await api.post(`/admin/${resource}`, stripMeta(editing));
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${resource}/${id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <div data-testid={`admin-${resource}-page`}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <button data-testid={`add-${resource}-btn`} onClick={() => setEditing({ ...defaults })} className="btn-red">
            <Plus className="h-4 w-4" /> Add new
          </button>
        }
      />

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[var(--pz-line)]">
                {columns.map((c) => <th key={c.key} className="px-5 py-3 font-bold">{c.label}</th>)}
                <th className="px-5 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} data-testid={`${resource}-row-${row.id}`} className="border-b border-[var(--pz-line)]/50 hover:bg-white/[0.03]">
                  {columns.map((c) => (
                    <td key={c.key} className="px-5 py-3 text-sm text-zinc-200">
                      {c.render ? c.render(row) : renderDefault(row[c.key])}
                    </td>
                  ))}
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(row)} data-testid={`edit-${row.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 text-[var(--pz-red)] inline-grid"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(row.id)} data-testid={`delete-${row.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-rose-500/10 text-rose-400 inline-grid ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-zinc-500">Nothing here yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {editing !== null && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-[var(--pz-ink-2)] border-l border-[var(--pz-line)] shadow-2xl overflow-auto text-white">
            <div className="p-5 border-b border-[var(--pz-line)] sticky top-0 bg-[var(--pz-ink-2)] flex items-center justify-between z-10">
              <div className="font-display text-2xl uppercase font-bold tracking-wide">{editing.id ? "Edit" : "Add new"}</div>
              <button type="button" onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center rounded-full border border-[var(--pz-line)] hover:border-[var(--pz-red)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              {renderForm(editing, setEditing)}
            </div>
            <div className="p-5 border-t border-[var(--pz-line)] sticky bottom-0 bg-[var(--pz-ink-2)] flex gap-2 justify-end z-10">
              <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
              <button data-testid={`save-${resource}-btn`} disabled={busy} className="btn-red disabled:opacity-60">{busy ? "Saving..." : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function stripMeta(o) {
  const { id, _id, created_at, updated_at, ...rest } = o || {};
  return rest;
}

function renderDefault(v) {
  if (v === true) return <span className="text-emerald-400 text-xs uppercase tracking-widest font-bold">Yes</span>;
  if (v === false) return <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold">No</span>;
  if (typeof v === "string" && v.length > 60) return v.slice(0, 60) + "…";
  if (typeof v === "string" && (v.startsWith("http") || v.startsWith("/api/")) && /\.(jpe?g|png|webp|gif)/i.test(v))
    return <img src={imgUrl(v)} alt="" className="h-10 w-16 object-cover rounded-md" />;
  return v ?? "";
}
